import axios from 'axios';
import { installAuthInterceptor } from '../lib/authAxios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});
installAuthInterceptor(client);

export async function getDashboard() {
  const { data } = await client.get('/api/crm/dashboard');
  return data;
}

export async function getAnalytics() {
  const { data } = await client.get('/api/crm/analytics');
  return data;
}

export async function getBotToken() {
  const authToken = localStorage.getItem('authToken') || '';
  const { data } = await client.get('/api/admin/bot-token', {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data.token;
}

export async function createEstimateFromLead(leadName, payload) {
  const { data } = await client.post(`/api/crm/leads/${encodeURIComponent(leadName)}/estimate`, payload);
  return data;
}

export async function createEstimateFromCustomer(customerName, payload) {
  const { data } = await client.post(`/api/crm/customers/${encodeURIComponent(customerName)}/estimate`, payload);
  return data;
}

// Records a payment already taken elsewhere (cash / check / Zelle / terminal).
// Does NOT charge anything — see chargeCard() for that.
export async function payInvoice(invoiceName, payload) {
  const { data } = await client.post(`/api/crm/invoices/${encodeURIComponent(invoiceName)}/payment`, payload);
  return data;
}

// Public Accept.js keys (API login ID + client key) for the in-app card form.
// These only permit browser-side tokenization; the transaction key stays server-side.
export async function getPaymentClientConfig() {
  const { data } = await client.get('/api/crm/payments/client-config');
  return data;
}

// Charges a card LIVE via Authorize.net. `payload` carries only the opaque
// Accept.js nonce ({ descriptor, value }) plus the originating surface —
// never a card number. Real money moves on this call.
export async function chargeCard(invoiceName, payload) {
  const { data } = await client.post(`/api/crm/invoices/${encodeURIComponent(invoiceName)}/charge-card`, payload);
  return data;
}

export async function getLeads() {
  const { data } = await client.get('/api/crm/leads');
  return data;
}

export async function getCustomers() {
  const { data } = await client.get('/api/crm/customers');
  return data;
}

// Fetches every customer (paginated server-side), not just the first 200 —
// used by the Contacts tab, which needs the complete list, not a preview.
export async function getAllCustomers() {
  const { data } = await client.get('/api/crm/customers', { params: { all: true } });
  return data;
}

export async function getCustomerDetail(customerId) {
  const { data } = await client.get(`/api/crm/customers/${encodeURIComponent(customerId)}`);
  return data;
}

// Phase 2B — unified customer/lead history (estimates, invoices, work orders,
// payments, service locations, communications, notes, and a merged timeline)
// for the ContactDetail history workspace. `type` is "customer" or "lead".
// Response envelope: { data: { contact_type, estimates, invoices, work_orders,
// payments, service_locations, communications, notes, timeline }, counts: {...} }
export async function getContactHistory(contactId, type) {
  const { data } = await client.get(`/api/crm/contacts/${encodeURIComponent(contactId)}/history`, {
    params: { type },
  });
  return data;
}

// Active Service Locations for this customer -- Commercial Customers &
// Multiple Service Locations, Tech App integration (2026-08-12).
export async function getCustomerServiceLocations(customerId) {
  const { data } = await client.get(`/api/crm/customers/${encodeURIComponent(customerId)}/service-locations`);
  return data;
}

// Adds a new Service Location for this customer (e.g. "+ Create a new
// Service Location" when none exist yet, or a genuinely new property).
export async function createCustomerServiceLocation(customerId, payload) {
  const { data } = await client.post(`/api/crm/customers/${encodeURIComponent(customerId)}/service-locations`, payload);
  return data;
}

export async function getScheduledQuotations(startDate, endDate, techFilter = null) {
  const params = { start: startDate, end: endDate };
  if (techFilter) params.tech = techFilter;
  const { data } = await client.get('/api/crm/scheduled-quotations', { params });
  return data;
}

export function getStoredRole() {
  return localStorage.getItem('userRole') || 'admin';
}

export function getStoredTechnicianName() {
  return localStorage.getItem('technicianName') || '';
}

export async function createLead(payload) {
  const { data } = await client.post('/api/crm/leads', payload);
  return data;
}

export async function getInvoices() {
  const { data } = await client.get('/api/crm/invoices');
  return data;
}

export async function getEstimates() {
  const { data } = await client.get('/api/crm/estimates');
  return data;
}

export async function getEstimateDetail(name) {
  const { data } = await client.get(`/api/crm/estimates/${encodeURIComponent(name)}`);
  return data;
}

export async function getInvoiceDetail(name) {
  const { data } = await client.get(`/api/crm/invoices/${encodeURIComponent(name)}`);
  return data;
}

export async function updateEstimate(name, payload) {
  const { data } = await client.put(`/api/resource/Quotation/${encodeURIComponent(name)}`, payload);
  return data;
}

// In-person "Request Customer Approval" flow (TechApproval.jsx) — attaches the captured
// signature to the real Quotation.custom_signature_image field (same system of record the
// print format and customer portal read), NOT a side channel. See tech-backend's
// erp_crm_proxy.py crm_estimate_signature() for the write path.
export async function submitEstimateSignature(name, { signatureDataUrl, customerName }) {
  const { data } = await client.post(`/api/crm/estimates/${encodeURIComponent(name)}/signature`, {
    signature_data_url: signatureDataUrl,
    customer_name: customerName,
  });
  return data;
}

// Commercial Customers & Multiple Service Locations, Tech App integration
// (2026-08-12): editing the location set on an ALREADY-CREATED Estimate
// (TechJobDetails.jsx), distinct from TechCreateJob.jsx's staged-at-creation
// flow above. Thin proxies over the existing Phase 7 whitelisted methods —
// server (not this file) is the ownership/active/edit-lock authority.
export async function getEstimateServiceLocations(estimateName) {
  const { data } = await client.get(`/api/crm/estimates/${encodeURIComponent(estimateName)}/service-locations`);
  return data;
}

export async function addEstimateServiceLocation(estimateName, serviceLocationId) {
  const { data } = await client.post(`/api/crm/estimates/${encodeURIComponent(estimateName)}/service-locations`, {
    service_location: serviceLocationId,
  });
  return data;
}

export async function removeEstimateServiceLocation(estimateName, serviceLocationId) {
  const { data } = await client.delete(
    `/api/crm/estimates/${encodeURIComponent(estimateName)}/service-locations/${encodeURIComponent(serviceLocationId)}`
  );
  return data;
}

// ── Tech job (Quotation) detail — proxied via tech-backend, never direct to ERP ──

export async function getQuotationDetail(name) {
  try {
    const { data } = await client.get(`/api/resource/Quotation/${encodeURIComponent(name)}`);
    return { success: true, data: data.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Canonical technician job-progress values (Quotation.custom_job_status, Select field).
// These five strings (blank = pre-schedule) are the only valid ERP values — do not add synonyms.
export const JOB_STATUSES = ['Scheduled', 'En Route', 'In Progress', 'Completed', 'Cancelled'];

// Customer appointment-confirmation vocabulary (Quotation.custom_appointment_status, Select
// field) — a DIFFERENT concept from job progress. Single source of truth so this can't drift.
export const APPOINTMENT_STATUSES = ['', 'Pending Confirmation', 'Confirmed', 'Cancelled', 'Reschedule Requested'];

// Normalize blank/null/undefined custom_job_status to 'Scheduled' for Quotations created
// before this field existed.
export function normalizeJobStatus(value) {
  return value || 'Scheduled';
}

// Display status for the tech UI: surface "Sent for Approval" (Quotation.custom_approval_status)
// on top of job progress, so a tech can see an estimate is out for customer approval instead of
// a stale "Scheduled". Display-only — the underlying custom_job_status still drives the workflow
// action buttons, so passing this through getJobStatusColor()/badges does not affect logic.
export function displayStatus(jobStatus, approvalStatus) {
  if ((approvalStatus || '') === 'Sent for Approval') return 'Sent for Approval';
  return normalizeJobStatus(jobStatus);
}

// Writes custom_job_status and THROWS on failure (unlike the old updateQuotationStatus).
// Callers MUST await + try/catch and MUST NOT update local UI state until this resolves.
export async function updateJobStatus(name, newStatus) {
  const { data } = await client.put(`/api/resource/Quotation/${encodeURIComponent(name)}`, {
    custom_job_status: newStatus,
  });
  return data.data;
}

// Shared color map for the tech job-progress vocabulary only — NOT appointment-status,
// NOT the generic Dashboard.jsx status color used for contacts/invoices.
export function getJobStatusColor(status) {
  const colors = {
    'Scheduled': 'bg-blue-100 text-blue-700 border-blue-300',
    'En Route': 'bg-purple-100 text-purple-700 border-purple-300',
    'In Progress': 'bg-green-100 text-green-700 border-green-300',
    'Completed': 'bg-gray-100 text-gray-700 border-gray-300',
    'Cancelled': 'bg-red-100 text-red-700 border-red-300',
    'Sent for Approval': 'bg-amber-100 text-amber-800 border-amber-300',
  };
  // Pass known statuses (incl. 'Sent for Approval') straight through; only blanks default to Scheduled.
  return colors[status] || colors[normalizeJobStatus(status)] || 'bg-gray-100 text-gray-700';
}

// Unified pipeline-status badge (Quotation.custom_pipeline_status) — the single
// server-computed status from docs/superpowers/plans/2026-07-25-unified-estimate-status-badge.md
// (Phase 1-3, ERP + backend/tech-backend, already shipped). Phase 4: display only.
// LOCKED 14-label color map (added 'Invoiced' 2026-08-13 — see
// _compute_pipeline_status's combined-invoice rung, events/quotation.py)
// — keep identical to the ERP desk's PIPELINE_STATUS_COLORS
// (erpnext_custom_app/integrity_flow_custom/public/js/custom.js) and
// QUOTATION_LIST_PIPELINE_STATUS_COLORS (.../public/js/quotation_list.js), modulo the
// Tailwind-vs-Frappe-indicator-color naming difference (green/gray/blue/red/amber here).
export const PIPELINE_STATUS_COLORS = {
  'Paid':              'bg-green-100 text-green-800 border-green-300',
  'Completed':         'bg-green-100 text-green-800 border-green-300',
  'Approved':          'bg-green-100 text-green-800 border-green-300',
  'Invoiced':          'bg-green-100 text-green-800 border-green-300',
  'Closed':            'bg-gray-100 text-gray-700 border-gray-300',
  'Draft':             'bg-gray-100 text-gray-700 border-gray-300',
  'In Progress':       'bg-blue-100 text-blue-700 border-blue-300',
  'Confirmed':         'bg-blue-100 text-blue-700 border-blue-300',
  'Scheduled':         'bg-blue-100 text-blue-700 border-blue-300',
  'Declined':          'bg-red-100 text-red-700 border-red-300',
  'Past Due':          'bg-red-100 text-red-700 border-red-300',
  'Canceled':          'bg-red-100 text-red-700 border-red-300',
  'Sent for Approval': 'bg-amber-100 text-amber-800 border-amber-300',
  'Reschedule Needed': 'bg-amber-100 text-amber-800 border-amber-300',
};

// Tailwind class for a pipeline-status label; unknown/unmapped labels default to gray
// (never throws, never renders an unstyled badge).
export function pipelineStatusColor(label) {
  return PIPELINE_STATUS_COLORS[label] || 'bg-gray-100 text-gray-700 border-gray-300';
}

// The single label to render for a Quotation/estimate/job badge, everywhere.
// HARD RULE (owner-mandated graceful fallback): prefer the server-computed
// custom_pipeline_status; if it's ever blank/missing on a record (e.g. an older
// row that predates the field, or a payload shape that doesn't include it), fall
// back to the existing displayStatus()/normalizeJobStatus() logic so a badge is
// NEVER blank. Never call this with just a status string — pass the
// quotation/job/estimate object itself so the fallback has what it needs.
export function pipelineStatusLabel(q) {
  if (!q) return normalizeJobStatus(undefined);
  return q.custom_pipeline_status || displayStatus(q.custom_job_status, q.custom_approval_status);
}

export async function log3CXCall(quotationId, callData) {
  try {
    const { data } = await client.post('/api/resource/Communication', {
      communication_type: 'Communication',
      communication_medium: 'Phone',
      subject: `Call to ${callData.customer_name}`,
      content: `3CX Call - Duration: ${callData.duration || 'N/A'}`,
      reference_doctype: 'Quotation',
      reference_name: quotationId,
      phone_no: callData.phone_number,
      status: 'Open',
    });
    return { success: true, data: data.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function syncQuotationToGCal(quotationId) {
  const { data } = await client.post(
    '/api/method/integrity_flow_custom.api.sync_estimate_to_gcal',
    { quotation_id: quotationId }
  );
  return data;
}

export async function sendAppointmentConfirmation(quotationId) {
  const { data } = await client.post(
    '/api/method/integrity_flow_custom.api.send_appointment_confirmation',
    { quotation_id: quotationId }
  );
  return data;
}

// Appointment windows (owner 2026-10-12): 8-11 / 11-2 / 2-5, any time, or a staff-booked full day.
// These are the exact Quotation.custom_scheduled_time options.
export const TIME_WINDOWS = ['8AM-11AM', '11AM-2PM', '2PM-5PM', 'Any Time', 'All Day'];

// The picker list for a record: the current windows, plus the record's own value when it is an
// older 2-hour window (e.g. '1PM-3PM') so opening an old appointment shows what is really saved.
export function timeWindowOptions(current) {
  return current && !TIME_WINDOWS.includes(current) ? [...TIME_WINDOWS, current] : TIME_WINDOWS;
}

// value = ERPNext User (email) that custom_technician links to; label = display name
export const TECHNICIANS = [
  { value: 'tech1@aaairrigationservice.com', label: 'tech1' },
  { value: 'tech2@aaairrigationservice.com', label: 'tech2' },
  { value: 'jdboatman1@yahoo.com', label: 'John Boatman' },
  { value: 'norland.cassandra@yahoo.com', label: 'Cassandra' },
];

// Parse single email or comma-separated emails into an array of clean strings.
export function parseTechnicians(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

// Format an array of technician emails into a canonical comma-separated string.
export function formatTechnicians(techList) {
  if (!techList || !techList.length) return '';
  return Array.from(new Set(techList.map((s) => s.trim()).filter(Boolean))).join(', ');
}

// Map a custom_technician value (single email or comma-separated emails) back to display label(s).
export function getTechnicianLabel(value) {
  if (!value) return '';
  const parts = parseTechnicians(value);
  if (parts.length === 0) return '';
  return parts
    .map((part) => {
      const match = TECHNICIANS.find((t) => t.value === part);
      return match ? match.label : part;
    })
    .join(', ');
}

export async function uploadPhoto({ doc_type, doc_id, image_base64, filename, content_type, uploaded_by = '', line_idx = null, photo_type = null }) {
  const { data } = await client.post('/api/photos/upload', { doc_type, doc_id, image_base64, filename, content_type, uploaded_by, line_idx, photo_type });
  return data;
}

export async function deletePhoto(photoId) {
  const { data } = await client.delete(`/api/photos/${encodeURIComponent(photoId)}`);
  return data;
}

export async function getEtaFromCoords(originLat, originLng, destination) {
  const { data } = await client.get('/api/maps/eta', {
    params: { origin_lat: originLat, origin_lng: originLng, destination },
  });
  return data;
}

export async function updateTechLocation({ tech_name, lat, lng, job_id = '', accuracy = 0 }) {
  const { data } = await client.post('/api/tech/location', { tech_name, lat, lng, job_id, accuracy });
  return data;
}

export async function getTechLocations() {
  // Backend returns { locations: [...] } (see tech-backend/server.py
  // get_tech_locations), not { data: [...] } like most other list
  // endpoints here. TechMap.jsx reads result.data, so this was always
  // silently resolving to [] — the Field Map showed 0 active technicians
  // even with real GPS rows in tech_locations. Normalize here so callers
  // keep the { data: [...] } convention. (2026-09-22)
  const { data } = await client.get('/api/tech/locations');
  return { data: data?.locations || [] };
}

export async function geocodeAddress(address) {
  const { data } = await client.post('/api/maps/geocode', { address });
  return data;
}

export async function suggestAddress(query) {
  const { data } = await client.post('/api/address/suggest', { query });
  return data;
}

// ── Work Orders ────────────────────────────────────────────────────────────

export async function createWorkOrder(sourceEstimate, technicianName = '', createdBy = '') {
  const { data } = await client.post('/api/crm/work-orders', {
    source_estimate: sourceEstimate,
    technician: technicianName,
    created_by: createdBy,
  });
  // tech-backend's POST /crm/work-orders wraps the Work Order in an envelope
  // ({data: {...wo_id, ...}, status: "created"|"existing"}) -- every caller
  // (TechJobDetails.jsx x2, Dashboard.jsx) reads the result as a flat WO
  // object (result.wo_id), so unwrap here once rather than at each call
  // site. Bug found live 2026-08-13: "Create WO" redirected to
  // /tech/workorder/undefined because result.wo_id was reading the outer
  // envelope, which has no wo_id key of its own.
  return data.data;
}

export async function getWorkOrders(tech = null) {
  const params = tech ? { tech } : {};
  const { data } = await client.get('/api/crm/work-orders', { params });
  return data;
}

export async function getWorkOrderDetail(woId) {
  const { data } = await client.get(`/api/crm/work-orders/${encodeURIComponent(woId)}`);
  return data;
}

export async function updateWorkOrderItem(woId, idx, completed, completedBy = '') {
  const { data } = await client.put(`/api/crm/work-orders/${encodeURIComponent(woId)}/items/${idx}`, {
    completed,
    completed_by: completedBy,
  });
  return data;
}

export async function createSupplementalEstimate(woId) {
  const { data } = await client.post(`/api/crm/work-orders/${encodeURIComponent(woId)}/supplemental`);
  return data;
}

export async function addSupplementalToWO(woId, estName) {
  const { data } = await client.post(
    `/api/crm/work-orders/${encodeURIComponent(woId)}/add-supplemental/${encodeURIComponent(estName)}`
  );
  return data;
}

export async function generateWOInvoice(woId, completedBy = '') {
  const { data } = await client.post(`/api/crm/work-orders/${encodeURIComponent(woId)}/invoice`, {
    completed_by: completedBy,
  });
  return data;
}

// Manual-only action (nothing auto-sends) — emails the customer a Pay Now link + Zelle info.
export async function sendInvoiceToCustomer(invoiceId) {
  const { data } = await client.post(`/api/crm/invoices/${encodeURIComponent(invoiceId)}/send`);
  return data;
}

// Manual-only action — emails the customer the estimate with one-click
// Approve/Decline links and sets custom_approval_status = "Sent for Approval".
// Proxies ERPNext's send_estimate_to_customer, the same method the desk button uses.
export async function sendEstimateToCustomer(estimateId) {
  const { data } = await client.post(`/api/crm/estimates/${encodeURIComponent(estimateId)}/send`);
  return data;
}

export function shortWOId(erpName) {
  const m = (erpName || '').match(/(\d{4})-0*(\d+)$/);
  return m ? `WO${m[1].slice(2)}${m[2].padStart(3, '0')}` : (erpName || '').replace(/[^a-zA-Z0-9]/g, '');
}

export async function sendChatMessage(text, fromTech = '', jobId = '') {
  const parts = [];
  if (fromTech) parts.push(`*From:* ${fromTech}`);
  if (jobId) parts.push(`*Job:* ${jobId}`);
  parts.push(text);
  const { data } = await client.post('/api/chat/send', { text: parts.join('\n') });
  return data;
}

export function formatCurrency(amount) {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}
